// Centro de notificaciones in-app (VLT-361 · VLT-114/116 frontend).
// REGLA DE HONESTIDAD: nada se inventa — cada aviso se DERIVA de datos reales
// de los servicios existentes (RLS decide qué ve cada quien) y navega a la
// pantalla donde se resuelve. La parte de clasificación es PURA y testeable.
import type { Task } from './tasks';
import type { SupportTicket } from './support';
import type { Appointment } from './appointments';
import type { InfoRequest } from './requests';
import { tasksService } from './tasks';
import { supportService } from './support';
import { appointmentsService } from './appointments';
import { requestsService, pendingItems } from './requests';

export type NotificationKind = 'task_overdue' | 'appointment_today' | 'ticket_open' | 'request_pending';

export interface AppNotification {
  id: string;
  kind: NotificationKind;
  /** Clave i18n del texto (notifications.items.*) + parámetros. */
  params: Record<string, string | number>;
  /** A dónde lleva el clic — siempre la pantalla donde se resuelve. */
  to: string;
  /** Para ordenar: lo más urgente primero. */
  at: string; // ISO
}

const day = (iso: string) => iso.slice(0, 10);

/** PURA: clasifica tareas → avisos (vencidas o que vencen hoy, no cerradas). */
export function fromTasks(tasks: Task[], today: string): AppNotification[] {
  return tasks
    .filter((t) => t.status !== 'done' && t.status !== 'cancelled' && t.dueDate && day(t.dueDate) <= today)
    .map((t) => ({
      id: `task-${t.id}`,
      kind: 'task_overdue' as const,
      params: { title: t.title },
      to: '/tasks',
      at: t.dueDate ?? today,
    }));
}

/** PURA: citas de HOY aún agendadas. */
export function fromAppointments(appts: Appointment[], today: string): AppNotification[] {
  return appts
    .filter((a) => a.status === 'scheduled' && day(a.startsAt) === today)
    .map((a) => ({
      id: `appt-${a.id}`,
      kind: 'appointment_today' as const,
      params: {
        name: a.clientName,
        time: new Date(a.startsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      },
      to: '/appointments',
      at: a.startsAt,
    }));
}

/** PURA: tickets abiertos o esperando respuesta (según lo que RLS devolvió). */
export function fromTickets(tickets: SupportTicket[]): AppNotification[] {
  return tickets
    .filter((t) => t.status === 'open' || t.status === 'waiting_user')
    .map((t) => ({
      id: `ticket-${t.id}`,
      kind: 'ticket_open' as const,
      params: { no: t.ticketNo, subject: t.subject },
      to: '/support',
      at: t.updatedAt ?? t.createdAt,
    }));
}

/** PURA (rol cliente): solicitudes con requisitos aún pendientes. */
export function fromRequests(reqs: InfoRequest[]): AppNotification[] {
  return reqs
    .filter((r) => r.status !== 'received' && pendingItems(r.items).length > 0)
    .map((r) => ({
      id: `req-${r.id}`,
      kind: 'request_pending' as const,
      params: { title: r.title, count: pendingItems(r.items).length },
      to: '/portal',
      at: r.sentAt ?? new Date().toISOString(),
    }));
}

export function sortNotifications(list: AppNotification[]): AppNotification[] {
  return [...list].sort((a, b) => a.at.localeCompare(b.at));
}

/** Agrega según el rol; cada fuente falla en silencio hacia lista vacía para
 *  que un servicio caído jamás rompa la campana. */
export async function gatherNotifications(role: string | null): Promise<AppNotification[]> {
  const today = new Date().toISOString().slice(0, 10);
  const safe = async <T>(p: Promise<T>, fallback: T): Promise<T> => {
    try { return await p; } catch { return fallback; }
  };
  if (role === 'client') {
    const [reqs, appts] = await Promise.all([
      safe(requestsService.listMine(), [] as InfoRequest[]),
      safe(appointmentsService.upcoming(), [] as Appointment[]),
    ]);
    return sortNotifications([...fromRequests(reqs), ...fromAppointments(appts, today)]);
  }
  const [tasks, appts, tickets] = await Promise.all([
    safe(tasksService.list(), [] as Task[]),
    safe(appointmentsService.upcoming(), [] as Appointment[]),
    safe(supportService.list(), [] as SupportTicket[]),
  ]);
  return sortNotifications([
    ...fromTasks(tasks, today),
    ...fromAppointments(appts, today),
    ...fromTickets(tickets),
  ]);
}
